const STORAGE_KEY = "pendingTemplateId";

/**
 * How long a "Get app" intent survives an interrupted sign-in.
 *
 * Long enough to cover signup with email verification (open inbox, click link,
 * come back); short enough that logging in days later isn't hijacked by a
 * template the visitor has long forgotten. Expiring early is cheap — they land
 * on their shelf and can re-open the share link — while not expiring silently
 * forks and publishes an app they never asked for.
 */
const TTL_MS = 60 * 60 * 1000; // 1 hour

/** Remember which template to return to after login/signup. */
export function rememberPendingTemplate(templateId: string): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ templateId, savedAt: Date.now() }),
  );
}

/**
 * Read the pending template and clear it in one step.
 *
 * Always clears, including when the entry is stale or unparseable — leaving it
 * behind means re-evaluating it on every future login, which is the bug this
 * guards against. Returns null for anything expired or malformed, so callers
 * only ever see a live intent.
 */
export function takePendingTemplate(): string | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  localStorage.removeItem(STORAGE_KEY);

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { templateId, savedAt } = parsed as {
      templateId?: unknown;
      savedAt?: unknown;
    };
    if (typeof templateId !== "string" || typeof savedAt !== "number") {
      return null;
    }
    return Date.now() - savedAt > TTL_MS ? null : templateId;
  } catch {
    // A bare template id written by a build from before this change. Dropping
    // it is the right call — we can't know how old it is.
    return null;
  }
}
